/*
 * English, and the schema every other language is checked against — `i18n.test.ts` fails a key
 * that exists here and not there, because a missing key renders as itself on screen.
 *
 * Deliberately never translated, and therefore not in here: game codes, team names, display
 * names, and money. `formatMoney` renders a symbol and a whole number in every language, because
 * the pot has to read the same to everybody in one pool.
 */
export default {
  /** The browser tab, per route. Keys, because a tab is text on a screen like any other. */
  titles: {
    home: 'Rugby Squares',
    join: 'Join a pool',
    game: 'Pool',
    hostNew: 'Start a pool',
    hostGame: 'Manage pool',
    localPlay: 'Local play',
    myGames: 'My pools',
    settings: 'Settings',
    signIn: 'Sign in',
    notFound: 'Not found',
    privacy: 'Privacy',
    howToPlay: 'How to play',
    contact: 'Contact',
  },

  app: {
    /** The product's name. The same in every language — it is a name, not a phrase. */
    name: 'Rugby Squares',
    /** The skip link, and the first thing a keyboard reaches on every screen. */
    skipToContent: 'Skip to the content',
    /** Turning down the one-time language offer, in the language they are already reading. */
    noThanks: 'No thanks',
    nav: {
      join: 'Join',
      host: 'Host',
      myGames: 'My pools',
      settings: 'Settings',
      signIn: 'Sign in',
      signOut: 'Sign out',
      /*
       * Only a guest is asked, and this is why: their session *is* the record. An account holder
       * signs out and signs back in; a guest signs out and every pool they host or play in is
       * unreachable. See `onSignOut` in App.vue.
       */
      confirmSignOut:
        'Sign out? Your pools live in this browser, and with no account there is no way back to them.',
      yesSignOut: 'Yes, sign out',
      staySignedIn: 'Stay signed in',
    },
    footer: {
      howToPlay: 'How to play',
      privacy: 'Privacy',
      disclaimer:
        'A tracker for private pools among people who know each other. No money moves through this site.',
      contact: 'Contact us',
    },
  },

  common: {
    squares: '{n} square | {n} squares',

    listAnd: 'and',
    /** The "?" beside a label whose hint is hidden behind it. Named after its field. */
    hintAria: 'What is {label} for?',
    errorPrefix: 'Error: {message}',
    joinAGame: 'Join a pool',
  },

  /**
   * Error codes the reader can do something about. `userMessage` falls back to the API's own
   * (English) sentence for anything not here, so a code the SPA can actually provoke needs a
   * key — see docs/API.md for the full list.
   */
  errors: {
    /** The catch-all a view builds when a rejection is not an ApiError at all. */
    UNKNOWN_ERROR: 'Something went wrong. Try again.',
    NETWORK_ERROR: 'Could not reach the server. Check your connection and try again.',
    SQUARE_TAKEN: 'Someone just took one of those squares. The grid has been refreshed.',
    LIMIT_EXCEEDED: "That would put you over this pool's limit per player.",
    NOT_ENOUGH_SQUARES: 'There are not that many squares left.',
    GAME_NOT_OPEN: 'This pool is not taking picks any more.',
    NOT_JOINED: 'Join the pool before picking squares.',
    SQUARES_EXIST: 'Squares have been claimed, so the teams and the price are settled.',
    LIMIT_BELOW_HOLDINGS: 'Somebody already holds more squares than that.',
    GAME_NOT_LOCKED: 'Lock the pool before entering the score.',
    ALREADY_COMPLETED: 'This pool already has a final score.',
    NOT_HOST: 'Only the host can do that.',
    SUPER_POWERS_DISABLED: "This pool was set up so the host cannot move anybody else's squares.",
    ALREADY_ASSIGNED: 'They already hold that square, so there is nothing to move.',
    TARGET_NOT_JOINED: 'That player is not in this pool any more.',
    PLAYER_CHANGED: 'Somebody picked up a square while that was running. Try again.',
    NO_PLAYERS:
      'Nobody has joined yet, so there is nobody to share the empty squares out to. Lock it once somebody is in, or pick a different rule for the empty ones.',
    NOT_CLAIMED:
      'Nobody is holding those squares any more — somebody let them go while you were choosing. The grid has been refreshed.',
    ACCOUNT_REQUIRED: 'That needs an email address behind your session. Sign in to carry on.',
    NOT_AUTHENTICATED: 'Your session has expired. Sign in again.',
    NOT_OWNER: 'That square belongs to someone else.',
    NOT_FOUND: 'We could not find that pool. Check the code.',
    /* The three the claim form can provoke. Each needs its own next step, which is why they are
       three codes rather than one. */
    PLAYER_NOT_FOUND:
      'Nobody played under that name in that pool. Check the spelling against the grid.',
    NOT_CLAIMABLE:
      'That player is not a guest, so there is nothing to move. Either somebody signed in already holds those squares, or the host added the name on their own device — ask them to hand it over.',
    AMBIGUOUS_PLAYER:
      'More than one player used that name in that pool, so we cannot tell which one is yours. Ask the host to hand the squares over instead.',
    CONFLICT: 'That pool changed while we were working on it. Try again.',
    INVALID_CODE: 'That code is not right. Check the email and try again.',
    CODE_EXPIRED: 'That code has expired. Ask for a new one.',
    TOO_MANY_ATTEMPTS: 'Too many attempts. Ask for a new code.',
    RATE_LIMITED: 'Too many requests. Give it a minute.',
    VALIDATION_ERROR: 'Some of those details are not valid.',
    INTERNAL_ERROR: 'Something went wrong on our side. Try again shortly.',
    /** One code, three endings — the API sends the real status in `details.status`. */
    notOpen: {
      LOCKED: 'This pool is locked — the host has stopped taking picks.',
      CANCELLED: 'This pool was cancelled.',
      COMPLETED: 'This pool is finished — the final score is in.',
    },
    /** The host's voice: they are spending somebody else's allowance, so "you" is wrong. */
    hostLimit:
      "That would put them over this pool's limit of {max} squares each. Raise the limit first if you mean to.",
    hostLimitUnknown:
      "That would put them over this pool's limit per player. Raise the limit first if you mean to.",
    /** Windows are fixed rather than sliding, so this rounds up: waiting less is the nicer error. */
    waitMinutes:
      'Too many requests. Try again in about {n} minute. | Too many requests. Try again in about {n} minutes.',
  },

  signIn: {
    heading: 'Sign in',
    intro: 'We will email you a six-digit code. No password, ever.',
    /**
     * docs/UX_FLOWS.md: the reason appears where the account is asked for, not later — and it has
     * to be the *true* reason. Hosting stopped needing an address when guests could host, so this
     * says the one thing an account still buys: a you that survives this browser.
     */
    why: 'Nothing here needs an account — you can play and host as a guest. An address gets you one thing: your pools follow you to another device, and back if you clear this browser.',
    codeSentTo: 'Enter the six-digit code we emailed to {email}.',
    emailLabel: 'Email address',
    emailPlaceholder: "you{'@'}example.com", // vue-i18n reads a bare @ as a link to another message.
    codeLabel: 'Six-digit code',
    requestCode: 'Email me a code',
    submitCode: 'Sign in',
    resendIn: 'Resend in {seconds}s',
    resend: 'Send another code',
    differentEmail: 'Use a different email',
    enterEmail: 'Enter your email address',
    codeIsSixDigits: 'The code is six digits',
    failed: 'Something went wrong signing you in. Ask for a new code and try again.',
  },

  home: {
    /*
     * The question the ad asks, because this page is the answer to that ad. It used to name the
     * category to people who already knew the category — "Score squares, for rugby." — and
     * "Score" mis-parsed as an imperative verb on the way past.
     */
    heading: 'Who calls the final score?',
    /*
     * What the game *is*, and it goes above the board rather than under it: the animation is
     * illustrating this sentence, so a reader who meets the board first is decoding a diagram.
     * It is also the only explanation a reduced-motion or screen-reader visitor gets — the
     * storyline over the board is `aria-hidden` and the result caption waits for the last beat.
     */
    explain:
      "Pick the square matching the last digit of each team's final score. Springboks 12, All Blacks 34 means column 2, row 4 takes the pot.",
    /*
     * The scam question, answered above the join/host pair — see views.test.ts. Naming all
     * three of a charge, a card and an account is the point: the reassurance a stranger is
     * scanning for is the absence of each, and "free" alone reads as a trial.
     *
     * Eleven words, down from twenty-two. Above the fold there were 39 words and 28 of them were
     * about price and signup — a page defending itself rather than explaining itself. The claim
     * that stayed is the one nobody can guess: a host's players need no account either.
     */
    free: 'Free to use. Always for fun.',
    noAccount: 'Nothing to pay, no card, no account — not even for your players.',
    haveCode: 'I have a code',
    howToPlay: 'New here? How it works',
    hostGame: 'Start a pool',
    /** Over the example board, so the page has something to scan besides its heading. */
    boardHeading: 'From first pick to final whistle',
    /**
     * After the board, where the story leaves the eye. The only interactive thing below the fold
     * used to be the footer, so somebody the animation had just convinced had to scroll back up.
     */
    closingHeading: 'Ready to start one?',
    /*
     * Held back until the storyboard has finished playing — see useHomeReveal. Printed with the
     * fixture from the first frame, it gives away the ending of the thing it is captioning.
     */
    exampleResult: '12–34 wins the highlighted square',
    /*
     * The storyboard over the example board: three lines, said *before* each chapter draws, so
     * the board illustrates the line rather than the line explaining it. There is no fourth —
     * the words clear and the gold square is the last beat on its own.
     */
    reveal: {
      yours: 'You pick your squares.',
      theirs: 'Your mates take theirs.',
      score: 'Final score: {a}–{b}.',
    },
  },

  create: {
    heading: 'Start a pool',
    /*
     * What the form now asks, which is all it asks: the per-player limits it used to promise
     * here are on the manage screen, where a host can see the grid they apply to.
     */
    intro: 'Two teams and a price per square. Everything else you can change later.',
    /*
     * The host's own name. Only shown to somebody with no session — hosting needs no account,
     * and this field is the whole of the signup, exactly as on the join screen.
     */
    hostNameLabel: 'Your name',
    hostNameHint:
      'What your players will see next to the pool, and what you use to claim it back later.',
    /** A plausible first name for the field's placeholder, not a word to translate. */
    hostNamePlaceholder: 'Johan',
    signInFirst: 'Sign in instead',
    /** Labelled by axis: which team runs along the top is the most misunderstood thing here. */
    teamALabel: 'Team A — along the top',
    teamAPlaceholder: 'Springboks',
    teamBLabel: 'Team B — down the side',
    teamBPlaceholder: 'All Blacks',
    teamAColourLabel: 'Team A’s colour',
    teamBColourLabel: 'Team B’s colour',
    colours: {
      gold: 'Gold',
      black: 'Black',
      sky: 'Sky blue',
      red: 'Red',
      blue: 'Blue',
      green: 'Green',
    },
    nameLabel: 'Name this pool (optional)',
    nameHint: 'Worth setting if you are running more than one pool for the same match.',
    /** The automatic name, said out loud: a placeholder is gone the moment anybody types. */
    nameIfBlank: 'Left blank, it will be called {name}.',
    kickOffLabel: 'Kick-off (optional)',
    kickOffHint:
      'Shown to your players, in their own time zone. Leave it blank if everybody already knows.',
    /*
     * The currency is *in* the label rather than in a box beside it. It is derived from the
     * browser locale and correct; as a free-text three-character field it was a decision nobody
     * makes, and a South African on a US-locale browser got USD with no picker to fix it.
     */
    priceLabel: 'Price per square ({currency})',
    pricePlaceholder: '10',
    /** Zero is a setting rather than a mistake, and nobody finds it unless the field says so. */
    priceHint: 'Set it to 0 for a pool with nothing riding on it — a shot, or bragging rights.',
    /*
     * Read by the *manage* screen, along with `kickOff*`, `perPlayer*`, `recommended` and
     * `maximum`. They are the pool's own settings vocabulary rather than the create form's — the
     * fields moved screens and the words did not change, and a second copy under another
     * namespace is how two screens start disagreeing about the same setting.
     */
    currencyLabel: 'Currency',
    /** The amounts are never localised — the pot has to read the same to everybody in one pool. */
    potBefore: 'A hundred squares at {currency} {price} each is a pot of',
    potAfter: 'if the grid fills.',
    /** What stands in for the pot preview at a price of zero. */
    noMoney:
      'No money in this one. Nobody is asked to pay, and no amount appears anywhere in the pool.',
    perPlayerLegend: 'How many squares each player may buy',
    recommended: 'Recommended',
    maximum: 'Maximum',
    perPlayerHint:
      'Recommended is the number players see suggested when they open the grid. Most hosts leave it at 10, which fills the board with about ten people.',
    submit: 'Start a pool',
    submitting: 'Creating…',
    tooManyGames:
      'You have created a lot of pools recently. Try again in about {n} minute. | You have created a lot of pools recently. Try again in about {n} minutes.',
    /** The automatic name, mirroring `gameName` in the API's domain/games.ts. */
    defaultName: '{teamA} vs {teamB}',
    defaultNameExample: 'Springboks vs All Blacks',
    errors: {
      teamName: 'Enter a team name',
      nameTooLong: 'That name is too long',
      wholeSquares: 'A whole number of squares',
      atLeastOne: 'At least one square',
      onlyAHundred: 'There are only a hundred squares',
      kickOff: 'A date and a time, like 2026-09-05 15:00',
      price: 'A whole amount, like 10',
      currency: 'Three letters, like ZAR',
      overMax: 'No more than the maximum of {max}',
    },
  },

  myGames: {
    heading: 'My pools',
    tryAgain: 'Try again',
    emptyHeading: 'Nothing here yet',
    emptyBody: 'Pools you join or host will show up here, so you can find them again.',
    joinWithCode: 'Join with a code',
    hostGame: 'Start a pool',
    active: 'Still running',
    historic: 'Finished',
    kickOff: 'Kick-off',
    status: {
      OPEN: 'Open for picks',
      LOCKED: 'Locked',
      COMPLETED: 'Finished',
      CANCELLED: 'Cancelled',
    },
    /** Which side of the pool this person is on — what a list row has to disambiguate. */
    hosting: 'Hosting',
    playing: 'Playing',
    /** "Springboks v All Blacks" — the team names are never translated. */
    /*
     * "vs", not "v": a pool with no name of its own is called `{teamA} vs {teamB}` by the API
     * (`create.defaultName`, and `gameName` in the domain), so the short spelling put two
     * spellings of the same fixture on the screen one under the other.
     */
    fixture: '{teamA} vs {teamB}',
    squaresHeld: '{n} square | {n} squares',
    /*
     * Claiming a pool played as a guest. One pool at a time, on the strength of the code and the
     * name it was played under — see docs/AUTH.md for what the API will and will not move.
     */
    claim: {
      open: 'Missing a pool? Find it',
      heading: 'Find a pool you played as a guest',
      intro:
        'Played on another phone without signing in? Give us the code and the name you used, and we will move that pool onto this account.',
      nameLabel: 'The name you used in that pool',
      nameHint: 'Exactly as it appears on the grid. Each pool can use a different name.',
      submit: 'Find it',
      submitting: 'Looking…',
      moved: '{name} is yours now — it is in the list below.',
      movedAsHost: 'You are the host of {name} now — it is in the list below.',
      needsAccount:
        'Played a pool as a guest on another device? Signing in gives it somewhere to live, and then you can move it here.',
      signIn: 'Sign in',
    },
  },

  /*
   * What is counted, and what is not. Deliberately short: it is prose in three languages, and a
   * page nobody finishes reading protects nobody.
   *
   * Every claim here is a fact about the rest of the system, so it is the one page in this app
   * that can quietly become false while every test stays green. The two durations are pinned in
   * `apps/api/test/unit/config.test.ts`; the rest is held by `test/unit/PrivacyView.test.ts`.
   */
  privacy: {
    heading: 'What we count',
    intro:
      'This is the whole of it. There is no advertising here, nothing is sold to anybody, and no profile of you is built.',
    counted: {
      heading: 'Visits, counted without knowing who you are',
      visits:
        'Which pages get opened, which country they were opened from, and which link sent you here. This is counted by the same provider that already delivers every page of this site, so it hands your visit to nobody new.',
      noIdentifier:
        'No cookie is set for counting, no identifier is stored, and nothing connects one visit to another. Two visits from you look like two visits from two strangers.',
      counters:
        'We also keep plain totals: how many people signed in, how many played as a guest, how many pools were made, how many squares were taken. Totals only — never who did any of it.',
    },
    stored: {
      heading: 'What is kept so the pool works',
      name: 'The name you type when you join, so the other players can see whose squares are whose.',
      email:
        'Your email address, only if you choose to sign in. Playing as a guest needs no address at all.',
      cookie:
        'A cookie that keeps you signed in for about three months. It is the only cookie here, and it is what a session is.',
      language:
        'Your language choice. It is kept in your browser, and once you have a session — as a guest or signed in with an address — it is saved against you as well, so the choice follows you from a phone to a laptop.',
      theme:
        'Whether you asked for the light or the dark look. Kept in your browser, and — once you have a session, as a guest or signed in — saved against you as well, so it follows you from a phone to a laptop. Leaving it set to match your device stores nothing at all.',
      ip: 'Your IP address, to stop somebody hammering the sign-in, making guest sessions or pools in bulk, or flooding the contact form. It stops counting after two hours at most, and is then deleted automatically by a background clean-up that can take a day or two to come round. The row records which limit it is counting against and nothing else — not your name, and not the pools you opened.',
    },
    shared: {
      heading: 'What leaves here',
      body: 'Sending a sign-in code, or a message from the contact form, hands your address and what you wrote to the email provider that delivers it. Hosting a pool sends the two team names and the kick-off date to a model run by the same provider that hosts this site, to guess where the final score will land — those fields only, never your name or anybody else’s. Those are the only things you type that go anywhere else. Nothing is sold to anybody, and there is no advertising network here to sell it to.',
      link: 'The link you share to invite somebody shows a preview card wherever it is pasted — the pool’s name, what a square costs, how full the grid is, and the name of the host if they have picked squares of their own. Drawing that card means the chat app fetches it from its own servers, so those details reach the app before anybody taps anything. Nothing new is kept here, and anybody holding the code could already see all of it.',
    },
    contact: {
      heading: 'Questions, or want it gone',
      body: 'Ask us to delete your account and everything attached to it, and we will.',
    },
  },

  /*
   * The rules, for somebody who has not played. docs/GAME_RULES.md is the specification the API
   * is built against, and it is in a private repository — so this is the only public form the
   * rules have, which is exactly why it says as little as it can get away with.
   *
   * Two things are deliberately not here. The three empty-square policies come from
   * `lifecycle.policies`, the same strings the host reads when locking, so the two can never
   * disagree; and the coordinates in the examples are interpolated, so all three languages state
   * the same square and no translator ever retypes the arithmetic.
   */
  howToPlay: {
    heading: 'How to play',
    intro:
      'Everybody picks squares on a 10×10 grid. When the match ends, the last digit of each team’s score picks one square, and whoever holds it takes the pot.',

    picking: {
      heading: 'Picking your squares',
      join: 'The host shares a code. You type your name and tap the squares you want — no account, and nothing to install.',
      fixed:
        'One team runs along the top and the other down the side, both numbered 0 to 9. The numbers are there from the start and never move, so you are predicting a score rather than buying a raffle ticket.',
    },

    winning: {
      heading: 'Which square wins',
      rule: 'Take the last digit of each team’s final score. The first picks the column, the second picks the row, and that one square takes the pot.',
      /** The landing page's example, deliberately the same one. Coordinates are interpolated. */
      example: 'Springboks {teamA}, All Blacks {teamB} — column {col}, row {row}.',
      trap: 'The last digit, never the tens: {teamA}–{teamB} is column {col}, row {row}, because {teamB} ends in a {row}. This is the one people get wrong.',
    },

    pot: {
      heading: 'What is in the pot',
      body: 'The host sets a price per square, and the pot is the squares people paid for times that price. Winner takes all.',
      free: 'A square can also cost nothing. Set the price to 0 and the pool is played for a shot, or for bragging rights — no pot, no ledger, and nobody is asked to pay.',
    },

    hosting: {
      heading: 'Hosting, and the one step to think about',
      lifecycle:
        'Start a pool, share the code, and let people pick. When the grid is as full as it is going to get, you lock it — picking stops, and the pot is whatever it is at that moment.',
      empty: 'Locking is also where you decide what happens to the squares nobody bought:',
      score:
        'Then you enter the final score and the winner falls out of it. You can unlock a pool and let more people in, but not once a score has been entered.',
    },

    money: {
      heading: 'The money',
      body: 'This app is the record, not the bank. It tracks what each player owes, what the pot is worth and who won it; settling up happens however your group already does it.',
    },
  },

  /*
   * The contact form. The address it replaced used to be printed in the footer of every screen —
   * see ContactView.vue on why nothing here may print one again.
   */
  contact: {
    heading: 'Get in touch',
    intro: 'Missing something, or found something broken? Tell us and we will read it.',
    messageLabel: 'What is on your mind',
    messageHint: 'A pool code helps if it is about a particular one.',
    emailLabel: 'Your email (optional)',
    emailHint: 'Only so we can reply. Leave it blank if you would rather not.',
    submit: 'Send it',
    submitting: 'Sending…',
    sentHeading: 'Thanks — that has gone through.',
    sentBody: 'If you left an address, you will hear back from us there.',
  },

  notFound: {
    heading: 'Nothing here',
    body: 'That link may be mangled. Try entering the pool code instead.',
  },

  grid: {
    /*
     * The name of the grid itself, for anybody who reaches it without seeing it.
     *
     * `role="grid"` needs a name, and the cells only have accessible names at all since
     * 2026-08-30: they carried an `aria-label` on a bare `div`, which browsers do not expose.
     */
    board: 'The squares board',
    /** Screen-reader labels. Empty cells stay visually blank; this is where they get a name. */
    cellTaken: 'Column {col}, row {row}: {name}',
    /** The reader's own, which the board colours in and a tap gives back. */
    cellYours: 'Column {col}, row {row}: {name} — yours',
    cellFree: 'Column {col}, row {row}: available',
    /** Appended to whichever of the three above applies, never replacing it. */
    cellPredicted: ' — our predicted score',
    cellWon: ' — won',
    predictedCaption:
      'The framed square is where we think the final score lands. A guess, not a result.',
    /** Shown while the prediction is still being worked out. Reading the play is what you do
        when you are guessing where the ball goes next, which is exactly the job here. */
    predictingCaption: 'Reading the play — working out where the final score might land…',
  },

  share: {
    /*
     * What the share sheet prefills, as opposed to the buttons around it below.
     *
     * This was built in `api/share.ts` as English string literals, in all three languages —
     * and it is the most-read text in the product, because it is what lands in the group
     * chat rather than what one person sees on their own screen. The pool's *name* is still
     * never translated: it is somebody's own words, like a team name.
     */
    inviteTitle: 'Rugby Squares',
    inviteTitleNamed: '{name} — Rugby Squares',
    inviteText: 'Join this squares pool. The code is {code}.',
    inviteTextNamed: 'Join the squares pool for {name}. The code is {code}.',

    heading: 'The code to share',
    codeAria: 'Pool code {code}, tap to copy',
    share: 'Share',
    copyCode: 'Copy code',
    copyLink: 'Copy link',
    copiedCode: 'Code copied.',
    copiedLink: 'Link copied.',
    copyFailed: 'Could not copy — select it and copy it yourself.',
  },

  timestamps: {
    created: 'Created',
    kickOff: 'Kick-off',
    /** One stored timestamp, two endings — a called-off pool did not "finish". */
    finished: 'Finished',
    cancelled: 'Cancelled',
  },

  odds: {
    caption: 'Some digits are worth more than others',
    introBefore:
      'Rugby scores in threes, fives and sevens, so the last digit of a final score is not a coin toss. Across {matches} international test matches from {fromYear} to {toYear} — teams averaging {mean} points, give or take {sd} —',
    introAfter: 'came up most often.',
    legendReal: 'Real test matches',
    legendWays: 'Possible combinations',
    evenDraw: '{pct}% — an even draw',
    columnLabel:
      'Digit {digit}: {real}% of real test-match scores, {ways}% of possible combinations.',
    conclusion:
      "Both teams' digits are independent, so a square is worth the two multiplied together. That makes {best}–{best} the best square on the board at {bestPct}% and {worst}–{worst} the worst at {worstPct}% — against 1% if every digit were equally likely. Worth knowing before you pick, and not worth losing sleep over.",
  },

  /** The grid and the money, shared by both game screens. */
  game: {
    loading: 'The grid',
    notFound: 'We could not find that pool',
    failed: 'Something went wrong',
    tryAgain: 'Try again',
    enterCode: 'Enter a code',
    /** Which team runs along the top is the most misunderstood thing about the grid. */
    axes: '{teamA} along the top, {teamB} down the side · {price} a square',
    /** The same line for a pool with no money in it — `playsForMoney` in api/grid.ts. */
    axesFree: '{teamA} along the top, {teamB} down the side',
    refresh: 'Refresh',
    manage: 'Manage this pool',
    pot: 'pot',
    /** The players' half of `allowHostSuperPowers`, said either way and said before they pick. */
    powersOn: 'The host can move squares between players and put somebody on an empty one.',
    powersOff:
      "The host cannot move anybody's squares — not even to put your name on an empty one.",
    cancelledHeading: 'This pool was cancelled',
    cancelledBody:
      'Nobody won and nobody owes anything. Whatever changed hands is between you and the host.',
    idle: 'Nothing has been touched here for an hour, so this stopped updating itself.',
    /*
     * Read out by a live region when a poll lands, and only then — see `boardNews` in
     * useGameScreen. Announcing every poll would be noise; this is the count, once the burst has
     * settled, for somebody who cannot see the board change under them.
     */
    moreTaken: '{n} more square has gone | {n} more squares have gone',
    showLatest: 'Show the latest',
    finalScore: 'Final score {teamA} {scoreA} — {teamB} {scoreB}. The winning square is ringed.',
    wantInHeading: 'Want in?',
    wantInBody: 'Squares need a name on them. It takes one field and no email address.',
    joinThisGame: 'Join this pool',
    /*
     * With the amount, because it is one tap and `n` comes from the host's recommendation: at R20
     * a square this button was a R200 commitment that said only "Pick 10 for me", and the amount
     * appeared afterwards in `yourSquares`. The free twin is not "· R0" — a pool with no money in
     * it never mentions money. Same pair as `local.pickForThem`.
     */
    pickForMe: 'Pick {n} for me · {amount}',
    pickForMeFree: 'Pick {n} for me',
    picking: 'Picking…',
    /** A tap *is* the claim, so the sentence has to say both halves of that. */
    tapToTake:
      'Tap a square to take it, and tap it again to let it go — {n} still available to you.',
    atLimit: "You have reached this pool's limit of {max} squares per player.",
    shareNote: 'Anybody with the code or the link can join while the pool is open.',
    yourSquares: 'Your {n} square · {amount} | Your {n} squares · {amount}',
    yourSquaresFree: 'Your {n} square | Your {n} squares',
    paymentLabel: 'Your payment',
    paid: 'Paid — the host has you down as settled up.',
    notPaid:
      'Not paid yet — the host has not marked you settled up. This app keeps the record and moves no money; how your group handles it is up to you.',
    otherPlayers: 'Other players',
    paidShort: 'Paid',
    notPaidShort: 'Not paid',
    allowanceReached: 'This pool allows {max} squares per player, and you have picked all you can.',
    somebodyGotThereFirst: 'Somebody got to {keys} first, so it is not yours.',
    onlyLeft:
      'Only {n} square is left, so nothing was claimed. | Only {n} squares are left, so nothing was claimed.',
  },

  host: {
    loading: 'Your pool',
    notYoursHeading: 'This is not your pool',
    notYoursBody: 'Somebody else is hosting {name}. You can still play it.',
    goToGrid: 'Go to the grid',
    resultHeading: '{teamA} {scoreA} — {teamB} {scoreB}',
    winningSquare: 'The winning square is {key}, ringed below.',
    shareNote: 'Send the code or the link to your players. You will see the grid fill up here.',
    teams: 'Teams',
    perSquare: 'Per square',
    eachMayBuy: 'Each player may buy',
    upTo: 'up to {max} squares',
    /** The promise, stated with the pool's own facts — a setting only the host sees is no signal. */
    powersHeading: "What you can do to other people's squares",
    powersOn:
      'You can move a square from one player to another, put somebody on an empty square, and remove a player who is holding squares. Everybody in this pool can see that you can. It is also what lets you run this pool on one screen, with everybody picking on your device.',
    powersOff:
      "You cannot move anybody else's squares — not even to put somebody on an empty one. Everybody in this pool can see that. That rules out running this pool on one screen with everybody picking on your device, too. You can still lock the grid, enter the score and mark people paid.",
    /** Before the button, because a single claimed square makes it permanent. */
    powersOneWay:
      'You can give this up whenever you like. Taking it back needs an empty grid, so once somebody has picked a square it is settled.',
    powersSettled: 'Squares have been claimed, so this pool stays as your players found it.',
    giveUpPowers: 'Give up those powers',
    takePowersBack: 'Take those powers back',
    powersGiven: 'You can no longer move anybody else’s squares. Everybody playing can see that.',
    powersRestored: 'You can move squares in this pool again. Everybody playing can see that.',
    cancelledHeading: 'This pool was cancelled',
    cancelledBody: 'Nobody can pick squares in it any more, nobody won, and nobody owes anything.',
    handover: {
      start: 'Change who holds a square',
      heading: 'Changing who holds a square',
      tapAny: 'Tap as many squares as you like — empty ones, or ones somebody already holds.',
      /** Named while there are few enough to recognise, counted once there are not. */
      tallyNamed: 'Square {keys} | Squares {keys}',
      tallyCounted: '{n} squares',
      tallyNobody: '{what} — nobody holds it | {what} — nobody holds them',
      tallyHolder: '{what} — {name} holds it',
      tallyAllHeld: '{what}, all of them already held',
      tallySomeHeld: '{what}, {held} of them already held',
      endUpWith: 'It should end up with | They should end up with',
      chooseWho: 'Choose who gets it | Choose who gets them',
      nobody: 'Nobody — put it back on the grid | Nobody — put them back on the grid',
      lockedNoPutBack:
        'This pool is locked, so squares cannot go back on the grid. Unlock it first if you need to take one off somebody.',
      clearSelection: 'Clear the selection',
      neverMind: 'Never mind',
      putBackOne: '{key} is back on the grid, and the pot is {amount} lighter.',
      putBackMany: '{n} squares are back on the grid, and the pot is {amount} lighter.',
      movedOne: "{key} is {name}'s now, and the pot did not change.",
      boughtOne: '{name} is on {key}. They owe {amount} for it.',
      movedMany: "{n} squares are {name}'s now, and the pot did not change.",
      boughtMany:
        '{name} has {n} more squares, and owes {amount} for the {bought} nobody had paid for.',
      /* The same six with the money taken out: nothing moved, so there is nothing to report. */
      putBackOneFree: '{key} is back on the grid.',
      putBackManyFree: '{n} squares are back on the grid.',
      movedOneFree: "{key} is {name}'s now.",
      boughtOneFree: '{name} is on {key}.',
      movedManyFree: "{n} squares are {name}'s now.",
      boughtManyFree: '{name} has {n} more squares.',
    },
    playInThisGame: 'Play in this pool',
    settings: {
      open: 'Change the settings',
      heading: 'Pool settings',
      nameA: 'Team A — along the top',
      nameB: 'Team B — down the side',
      colourA: 'Team A’s bar colour',
      colourB: 'Team B’s bar colour',
      namesSettled:
        'Squares have been sold and you gave up your host powers, so the names are settled. The colours are not.',
      save: 'Save',
      cancel: 'Never mind',
    },
    /*
     * Said here rather than under the create form's button, where it was the last thing a
     * first-time host read before committing — about a pool that did not exist yet. Now there is
     * one, and this is an offer rather than a warning.
     */
    guestHost:
      'This pool lives in this browser only. Sign in and it follows you to your other devices.',
    localPlay: 'Local play',
    ledgerHeading: 'Who owes what',
    /** The same list with no column of amounts: still who is in and what they hold. */
    ledgerHeadingFree: 'Who is playing',
    /** The price, when there is not one. A fact, rather than a value that failed to load. */
    free: 'Free',
    /*
     * Read in the *share panel*, next to the two buttons it is asking the host to press — it used
     * to be printed in the ledger, below the grid and below the action row, about two phone
     * screens from the block it names. "Nobody else", because `POST /games` joins the host: a
     * pool made a minute ago has one player in it and it is the person reading this.
     */
    nobodyJoined:
      'Nobody else is in yet. Send them the code or the link and they will appear here as they pick.',
    /*
     * The host's twin of `game.idle`. An idle hour on this screen means nobody has picked, and
     * the app's only answer used to be a notice about its own polling — true, and a technical
     * apology where the one useful action belongs. There are deliberately no email reminders.
     */
    idle: 'Nothing has happened here for an hour, so this stopped checking. Share the code again if you are still waiting on people.',
    ledgerBefore: '{collected} collected ·',
    ledgerOutstanding: '{outstanding} still outstanding',
    ledgerAfter:
      'from {n} player. Settle up however you normally do — this app keeps the record and moves no money. | from {n} players. Settle up however you normally do — this app keeps the record and moves no money.',
    markAria: 'Mark {name} paid',
    markUnpaidAria: 'Mark {name} unpaid',
    paid: 'Paid',
    markPaid: 'Mark paid',
    removeAria: 'Remove {name} from this pool',
    remove: 'Remove',
    theirSquares: 'Their squares',
    putBackOnGrid: 'Put them back on the grid',
    chooseWhoTakes: 'Choose who takes them',
    giveThemTo: 'Give them to {name}',
    lockedMustTransfer:
      'This pool is locked, so their squares cannot go back on the grid. Pick who takes them, or unlock the pool first.',
    notABan: 'Not a ban — they can join again from the same link while the pool is open.',
    moveAndRemove: 'Move them and remove',
    moveThem: 'Move them',
    removeThem: 'Remove them',
    keepThem: 'Keep them',
    squaresAreNow: "{name}'s squares are {taker}'s now.",
    offTheLedger: '{name} is off the ledger.',
    lockHeading: 'Stop taking picks',
    nothingToLock: 'Nobody has joined yet, so there is nothing to lock. Share the code first.',
    lockIntro:
      'Locking stops the grid. You choose it now rather than at creation, because until the grid stops filling you do not know how many empty squares there will be.',
    policyLegend: 'What happens to the empty squares?',
    locking: 'Locking…',
    lock: 'Lock the pool',
    scoreHeading: 'The final score',
    scoreIntro:
      'Empty squares: {policy}. Entering the score settles the pool — after this it cannot be reopened.',
    policyNotSet: 'not set',
    /** A score of 20 is row 0, not row 2 — the rule people get wrong, named before committing. */
    previewHeld: '{teamA} {scoreA}, {teamB} {scoreB} wins square {key} — held by {name}.',
    previewEmpty: '{teamA} {scoreA}, {teamB} {scoreB} wins square {key} — which nobody holds.',
    savingScore: 'Saving…',
    confirmScore: 'Confirm the final score',
    reopenHeading: 'Changed your mind?',
    reopenIntro: 'Reopening lets people pick again.',
    reopenTakesBack:
      'The squares that were shared out free will be taken back, so there is something left to pick.',
    reopen: 'Reopen for picks',
    confirmCancel: 'Cancel {name}? Nobody wins, nobody owes anything, and this cannot be undone.',
    yesCancel: 'Yes, cancel it',
    keepRunning: 'Keep it running',
    cancelGame: 'Cancel this pool',
    statusLabel: 'Status',
  },

  local: {
    loading: 'Loading…',
    notYours: 'This is not your pool, so there is nothing to run here.',
    openAsPlayer: 'Open it as a player instead.',
    /** The facts the room asks about, in one line. */
    summary: '{price} a square · {grid} · pot {pot}',
    idle: 'Nothing has been touched here for an hour, so this stopped checking for changes.',
    checkNow: 'Check now',
    finalScore: 'Final score · {teamA} {scoreA} — {scoreB} {teamB}',
    cancelled: 'This pool was cancelled. Nobody won, and nobody owes anything.',
    /** The one-way door, said where somebody is standing when they hit it. */
    noPowers:
      "You gave up the power to act on anybody's behalf in this pool, so there is nobody to pick for here. Taking it back needs an empty grid.",
    fromTheirPhones: 'Everyone can still play from their own phone.',
    closed: 'Picking is closed.',
    settled: 'The grid is settled.',
    whoIsPicking: 'Who is picking?',
    tapYourName: 'Tap your name to take your squares. Until somebody does, the grid does nothing.',
    isPicking: '{name} is picking · {held} so far',
    addSomebody: '+ Add somebody',
    theirName: 'Their name',
    addThem: 'Add them',
    neverMind: 'Never mind',
    pickForThem: 'Pick {n} for {name} · {amount}',
    pickForThemFree: 'Pick {n} for {name}',
    orTap: '…or tap the squares {name} wants.',
    /** Releasing is discoverable only by being told: the gesture is the same as un-selecting. */
    tapToPutBack: "Tapping one of {name}'s own squares puts it back on the grid.",
    allPicked: '{name} has picked all this pool allows.',
    /** The same button with nothing selected — the way out of a turn is where the way in was. */
    done: '{name} · done',
    putBack: '{key} is back on the grid, and the pot is {amount} lighter.',
    putBackFree: '{key} is back on the grid.',
    ledgerHeading: 'Who owes what',
    ledgerHeadingFree: 'Who is playing',
    nobodyYet: 'Nobody yet. Add the people in the room, or share the code below.',
    ledgerBefore: '{collected} collected ·',
    ledgerOutstanding: '{outstanding} still outstanding',
    ledgerAfter: '. This app keeps the record and moves no money.',
    markAria: 'Mark {name} paid',
    markUnpaidAria: 'Mark {name} unpaid',
    paid: 'Paid',
    markPaid: 'Mark paid',
    ownPhone:
      'Getting your own phone out? Sign in first, then join — joining as a guest and signing in afterwards leaves your squares behind on the guest.',
    /** Says what it is rather than implying a lockdown it cannot enforce. */
    notALock:
      'This screen keeps the settings, the lock and the score out of reach of a stray tap. It is not a lock: whoever is holding this device is still signed in as the host.',
    backToManaging: '‹ back to managing this pool',
    keepsItselfUpToDate: 'This screen keeps itself up to date.',
    nobodyPicking: 'Nobody is picking now. Tap a name to carry on.',
    somebodyGotThereFirst:
      'Somebody got to {keys} first. Nothing was claimed — the rest is still selected.',
    limit: 'This pool allows {max} squares each, so nothing was claimed.',
    claimed: '{n} square to {name}. | {n} squares to {name}.',
  },

  gridSummary: {
    sold: '{sold} of {total} squares sold',
    /** Two numbers, because a DISTRIBUTE_FREE deal fills the board without adding to the pot. */
    takenAndSold: '{taken} of {total} taken · {sold} sold',
  },

  lifecycle: {
    policies: {
      LEAVE_BLANK: {
        label: 'Leave them blank',
        consequence:
          'If an empty square wins, nobody wins. What happens to the pot is between you and your players.',
        consequenceFree: 'If an empty square wins, nobody wins.',
      },
      HOUSE: {
        label: 'The house wins',
        consequence:
          'If an empty square wins, you take the pot. Fair when you are funding a prize and carrying the risk.',
        consequenceFree:
          'If an empty square wins, you win it. Fair when you are the one putting up the prize.',
      },
      DISTRIBUTE_FREE: {
        label: 'Share them out free',
        consequence:
          'The empty squares are shared out among your players now, evenly and for nothing, so somebody always wins. They add nothing to the pot.',
        consequenceFree:
          'The empty squares are shared out among your players now, evenly, so somebody always wins.',
      },
    },
    /** What a player is owed the moment the host locks, which is not how the host chose it. */
    playerPolicy: {
      LEAVE_BLANK: 'Empty squares were left empty — if one wins, nobody wins.',
      HOUSE: 'Empty squares belong to the host — if one wins, the host takes the pot.',
      DISTRIBUTE_FREE:
        'The empty squares were shared out among the players for free, so somebody always wins. A free square adds nothing to the pot, and nothing to what its holder owes.',
    },
    /** The same three with nothing riding on them — a parallel branch, so parity is checkable. */
    playerPolicyFree: {
      LEAVE_BLANK: 'Empty squares were left empty — if one wins, nobody wins.',
      HOUSE: 'Empty squares belong to the host — if one wins, the host wins.',
      DISTRIBUTE_FREE:
        'The empty squares were shared out among the players, so somebody always wins.',
    },
    score: {
      required: 'Enter the score',
      whole: 'Whole points only',
      absurd: 'That is not a rugby score',
    },
    /** The payoff, in three voices: the host's, the player's, and a room's. */
    winner: {
      host: {
        player: '{name} takes the pot — {amount}. Settle up however you normally do.',
        house: 'An empty square won, so the house takes it: {amount} to you, the host.',
        nobody:
          'An empty square won, so nobody takes the pot. The {amount} is between you and your players.',
      },
      player: {
        you: 'You won — the pot is yours, {amount}. Settle up however your group does.',
        other: '{name} takes the pot — {amount}.',
        house: 'An empty square won, and the host takes the pot — {amount}.',
        nobody:
          'An empty square won, so nobody takes the pot. What happens to the {amount} is up to your group.',
      },
      /* And the same two voices with no amount to name: the prize is not this app's business. */
      hostFree: {
        player: '{name} wins it.',
        house: 'An empty square won, so the house takes it — that is you.',
        nobody: 'An empty square won, so nobody wins.',
      },
      playerFree: {
        you: 'You won.',
        other: '{name} wins it.',
        house: 'An empty square won, and the host takes it.',
        nobody: 'An empty square won, so nobody wins.',
      },
    },
    holdings: {
      /** `squareCount` includes dealt squares and `amountOwed` does not, so both are named. */
      dealt: '{held}, {dealt} dealt free',
    },
    removal: {
      nothingHeld: 'Takes {name} off the ledger. They are not holding any squares.',
      transfer:
        "Moves {name}'s {held} to {destination} and takes {name} off the ledger. Nobody pays more or less than they do now.",
      backOnGrid:
        'Takes {name} off the ledger and puts their {held} back on the grid, where anybody can claim them.',
    },
    handover: {
      /** `Three` up to five, then the numeral — a sentence should not spell out "twenty-three". */
      counts: { 0: 'no', 1: 'One', 2: 'Two', 3: 'Three', 4: 'Four', 5: 'Five' },
      putBack: {
        refusal:
          'Nobody holds {keys}. Tap it again to drop it from the selection. | Nobody holds {keys}. Tap those again to drop them from the selection.',
        nothingToPutBack: 'Nobody holds {keys}, so there is nothing to put back.',
        fromOwner:
          'Takes {what} off {owner} and puts it back on the grid, where anybody can claim it. {owner} stops owing {amount}, and the pot drops by {amount}. | Takes {what} off {owner} and puts them back on the grid, where anybody can claim them. {owner} stops owing {amount}, and the pot drops by {amount}.',
        fromSeveral:
          'Takes {what} off {holders} players and puts it back on the grid, where anybody can claim it. The pot drops by {amount}. | Takes {what} off {holders} players and puts them back on the grid, where anybody can claim them. The pot drops by {amount}.',
        fromOwnerFree:
          'Takes {what} off {owner} and puts it back on the grid, where anybody can claim it. | Takes {what} off {owner} and puts them back on the grid, where anybody can claim them.',
        fromSeveralFree:
          'Takes {what} off {holders} players and puts it back on the grid, where anybody can claim it. | Takes {what} off {holders} players and puts them back on the grid, where anybody can claim them.',
        confirm: 'Put {what} back on the grid',
      },
      choose: {
        oneHeld:
          '{holder} holds {key}. Choose who it goes to — the square is paid for, so no money moves.',
        oneEmpty: 'Nobody holds {key}. Whoever you give it to will owe {amount} for it.',
        manyEmpty:
          '{count} squares, none of them held. Whoever you give them to will owe {amount}.',
        manyMixed: '{count} squares, {held} of them already held. Choose who they go to.',
        oneHeldFree: '{holder} holds {key}. Choose who it goes to.',
        oneEmptyFree: 'Nobody holds {key}. Choose who it goes to.',
        manyEmptyFree: '{count} squares, none of them held. Choose who they go to.',
        moveIt: 'Move it',
        handOutIt: 'Hand it out',
        moveThem: 'Move them',
        handOutThem: 'Hand them out',
      },
      refusal: {
        alreadyHoldsOne: '{name} holds {key} already, so there is nothing to do.',
        alreadyHoldsSome:
          '{name} already holds {keys}. Tap it again to drop it from the selection. | {name} already holds {keys}. Tap those again to drop them from the selection.',
        overLimit:
          'That would leave {name} paying for {total} square, and this pool allows {max} each. Pick fewer, or raise the limit first. | That would leave {name} paying for {total} squares, and this pool allows {max} each. Pick fewer, or raise the limit first.',
        overLimitFree:
          'That would leave {name} on {total} square, and this pool allows {max} each. Pick fewer, or raise the limit first. | That would leave {name} on {total} squares, and this pool allows {max} each. Pick fewer, or raise the limit first.',
      },
      plan: {
        moveOne:
          'Moves {key} from {holder} to {name}. The pot does not change, because the square is already paid for — but {name} owes the {amount} for it now, not {holder}.',
        putOnOne:
          'Puts {name} on {key}. They will owe {amount} for it — nobody has paid for that square yet, so this buys it in their name.',
        putOnMany:
          'Puts {name} on {count}. They will owe {amount} for them — nobody has paid for those yet, so this buys them in their name.',
        moveMany:
          'Moves {count} to {name}. The pot does not change, because they are already paid for — but {name} owes the {amount} for them now.',
        mixed:
          'Gives {name} {count}: {held} that change hands, and {empty} nobody has paid for. The pot goes up by {intoThePot}, and {name} will owe {amount} more.',
        moveOneFree: 'Moves {key} from {holder} to {name}.',
        putOnOneFree: 'Puts {name} on {key} — nobody holds that square yet.',
        putOnManyFree: 'Puts {name} on {count} — nobody holds those yet.',
        moveManyFree: 'Moves {count} to {name}.',
        mixedFree: 'Gives {name} {count}: {held} that change hands, and {empty} nobody holds yet.',
      },
      confirm: {
        moveOne: 'Move {key} to {name}',
        putOnOne: 'Put {name} on {key}',
        putOnMany: 'Put {name} on {count}',
        moveMany: 'Move {count} to {name}',
        giveMany: 'Give {name} {count}',
      },
    },
  },

  validation: {
    code: {
      required: 'Enter the code',
      /** Names the shape rather than the alphabet: "no O, I, L or U" is true and unhelpful. */
      shape: 'A code is six characters, like K7M2QP',
    },
    name: {
      required: 'Enter a name',
      tooLong: 'That name is too long',
    },
    message: {
      required: 'Tell us what is wrong',
      tooLong: 'That is longer than we can take — the essentials will do',
    },
    email: {
      invalid: 'That does not look like an email address',
    },
  },

  join: {
    heading: 'Join a pool',
    checking: 'Just a moment…',
    intro: 'Tell us what to call you on the grid. No email, no password, nothing else.',
    /*
     * What this pool is, said before the form asks for a name. The fixture, the fill and the
     * kick-off borrow the words `myGames` and `gridSummary` already use — the same facts on two
     * screens should not be two sets of strings.
     */
    about: {
      price: '{amount} a square',
      free: 'No money in this one — played for a shot, or for bragging rights.',
    },
    codeLabel: 'Pool code',
    codeHint: 'From the link the host shared, or ask them for it.',
    /** An example code, in the app's own alphabet — not a word to translate. */
    codePlaceholder: 'K7M2QP',
    nameLabel: 'Your name',
    nameHint: 'What the other players will see on your squares.',
    namePlaceholder: 'Nadia',
    submit: 'Join the pool',
    submitting: 'Joining…',
    /**
     * The trap in the API's docs/AUTH.md: verifying an address that already has an account
     * switches this browser onto that account's subject, and the guest's squares are left
     * behind. Offered here, where the decision is cheap.
     */
    haveAccount: 'Have an account with us? Sign in before you pick, so your squares stay yours.',
    signInFirst: 'Sign in first',
    seeTheGrid: 'Have a look at the grid',
    tooManyJoins:
      'A lot of people have joined from this connection. Try again in about {n} minute. | A lot of people have joined from this connection. Try again in about {n} minutes.',
  },

  settings: {
    heading: 'Settings',
    name: {
      heading: 'Your name',
      intro: 'What people see next to your squares in a new pool.',
      label: 'Your name',
      /** A plausible first name for the field's placeholder, not a word to translate. */
      placeholder: 'Nadia',
      save: 'Save',
      saving: 'Saving…',
      saved: 'Saved.',
      keepsOldSquares:
        'Squares you have already claimed keep the name you claimed them under, and each pool can use a different name — you choose it when you join.',
      signedOut: 'You pick a name when you join a pool — no account needed.',
    },
    language: {
      heading: 'Language',
      label: 'Language',
      failed: 'That language would not load. Check your connection and try again.',
    },
    appearance: {
      heading: 'Appearance',
      label: 'Appearance',
      system: 'Match my device',
      light: 'Light',
      dark: 'Dark',
      failed:
        'That did not save, so it will not follow you to your other devices. It is applied here.',
    },
  },
};
